export default class File {
    static #generatedFileStorageKeyPrefix = "generatedFile.";

    /**
     * Sanitizes a filename for use in a URL and URI-encodes it.
     * @param {string} filename The filename to sanitize.
     * @returns {string} The sanitized, URI-encoded filename.
     */
    static sanitizeUrlFilename(filename) {
        let sanitizedFileName = filename
            .replaceAll(/[^.a-zA-Z0-9_-]/gi, '-');

        return encodeURIComponent(sanitizedFileName);
    }

    /**
     * Stashes a generated file as Base64 in local storage.
     * @param {string} transferId The generated file transfer identifier.
     * @param {Blob} blob The file to store.
     * @returns {Promise<void>}
     */
    static async stashFile(transferId, blob) {
        const base64DataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });

        window.localStorage.setItem(`${this.#generatedFileStorageKeyPrefix}${transferId}`, base64DataUrl);
    }

    /**
     * Unstashes and removes a generated file from local storage.
     * @param {string} transferId The generated file transfer identifier.
     * @returns {Promise<Blob|null>} The file, or null if not found.
     */
    static async unstashFile(transferId) {
        const storageKey = `${this.#generatedFileStorageKeyPrefix}${transferId}`;
        const base64DataUrl = window.localStorage.getItem(storageKey);

        if (base64DataUrl === null) {
            return null;
        }

        window.localStorage.removeItem(storageKey);

        if (base64DataUrl === "error") {
            throw new Error("Generated file failed");
        }

        const response = await fetch(base64DataUrl);

        return response.blob();
    }

    /**
     * Removes generated files that were not claimed by a viewer.
     * @returns {void}
     */
    static #clearStashedFiles() {
        for (let index = window.localStorage.length - 1; index >= 0; index--) {
            const storageKey = window.localStorage.key(index);
            if (storageKey && storageKey.startsWith(this.#generatedFileStorageKeyPrefix)) {
                window.localStorage.removeItem(storageKey);
            }
        }
        window.localStorage.removeItem("generatedFiles");
    }

    /**
     * Opens a generated-file viewer and supplies its file when ready.
     * @param {string} filename The display filename.
     * @param {() => Promise<Blob>} loadFile Loads the file to display.
     * @returns {Promise<boolean>} Whether the viewer window opened.
     */
    static async openViewer(filename, loadFile) {
        const transferId = crypto.randomUUID();
        const sanitizedFileName = this.sanitizeUrlFilename(filename);
        const viewerWindow = window.open(`/file/${transferId}/${sanitizedFileName}`, "_blank");

        if (!viewerWindow) {
            return false;
        }

        this.#clearStashedFiles();

        try {
            const blob = await loadFile();
            if (viewerWindow.closed) {
                return true;
            }
            await this.stashFile(transferId, blob);
        } catch (error) {
            if (!viewerWindow.closed) {
                window.localStorage.setItem(`${this.#generatedFileStorageKeyPrefix}${transferId}`, "error");
            }
            throw error;
        }

        return true;
    }
}
